import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { ARCHETYPE_META } from "@/data/quiz";

/**
 * Spider / radar chart for the 4 archetypes' point distribution.
 * Layout: Halcón top, Lobo right, Delfín bottom, León left.
 * Labels are absolutely positioned around the chart container.
 */
const ORDER = [
  { code: "halcon", label: "Halcón", dim: "Visión", pos: "top" },
  { code: "lobo", label: "Lobo", dim: "Análisis", pos: "right" },
  { code: "delfin", label: "Delfín", dim: "Conexión", pos: "bottom" },
  { code: "leon", label: "León", dim: "Acción", pos: "left" },
];

const ACCENT = "#29BC9B";

function AxisLabel({ node, pct, side }) {
  const meta = ARCHETYPE_META[node.code];

  // Position offsets per side
  const base = "absolute flex flex-col items-center gap-1 z-10 pointer-events-none";
  const pos =
    side === "top"
      ? "top-0 left-1/2 -translate-x-1/2 -translate-y-2"
      : side === "bottom"
      ? "bottom-0 left-1/2 -translate-x-1/2 translate-y-2"
      : side === "left"
      ? "top-1/2 left-0 -translate-y-1/2 -translate-x-2 items-end !flex-row gap-3"
      : "top-1/2 right-0 -translate-y-1/2 translate-x-2 items-start !flex-row gap-3";

  // For side labels, the text block sits next to the icon (different orientation)
  const isSide = side === "left" || side === "right";

  const Icon = (
    <div
      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
      style={{
        border: `1px solid ${ACCENT}55`,
        background: "#0A0A0A",
      }}
    >
      <img
        src={meta.img}
        alt={meta.name}
        className="w-5 h-5 object-contain"
        style={{ filter: "brightness(0) invert(1)" }}
      />
    </div>
  );

  const Text = (
    <div className={`text-${isSide ? (side === "left" ? "right" : "left") : "center"} leading-tight`}>
      <div className="font-display text-sm font-bold text-white">
        {node.label}
      </div>
      <div className="text-[10px] text-white/40">({node.dim})</div>
      <div
        className="font-display text-base font-extrabold mt-0.5"
        style={{ color: ACCENT }}
      >
        {pct}%
      </div>
    </div>
  );

  return (
    <div className={`${base} ${pos}`}>
      {side === "left" ? (
        <>
          {Text}
          {Icon}
        </>
      ) : side === "right" ? (
        <>
          {Icon}
          {Text}
        </>
      ) : side === "top" ? (
        <>
          {Icon}
          {Text}
        </>
      ) : (
        <>
          {Icon}
          {Text}
        </>
      )}
    </div>
  );
}

export default function SpiderChart({ scores }) {
  const total = Object.values(scores).reduce((a, b) => a + b, 0) || 1;

  const data = ORDER.map((o) => {
    const pts = scores[o.code] || 0;
    const pct = Math.round((pts / total) * 100);
    return { subject: o.label, value: pct, code: o.code };
  });

  return (
    <div className="w-full mx-auto">
      <div className="relative w-full aspect-square">
        {/* Axis labels around the chart */}
        {ORDER.map((node, idx) => (
          <AxisLabel
            key={node.code}
            node={node}
            pct={data[idx].value}
            side={node.pos}
          />
        ))}

        {/* Chart — symmetric padding so the polygon stays vertically centered */}
        <div className="absolute inset-0 px-14 py-14">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart
              data={data}
              cx="50%"
              cy="50%"
              outerRadius="95%"
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            >
              <PolarGrid
                stroke="rgba(255,255,255,0.10)"
                strokeDasharray="3 4"
                gridType="polygon"
              />
              <PolarAngleAxis dataKey="subject" tick={false} axisLine={false} />
              <PolarRadiusAxis
                angle={90}
                domain={[0, 50]}
                ticks={[10, 20, 30, 40, 50]}
                tick={{
                  fill: "rgba(255,255,255,0.22)",
                  fontSize: 9,
                  fontFamily: "Manrope",
                }}
                tickFormatter={(v) => `${v}%`}
                stroke="transparent"
                axisLine={false}
              />
              <Radar
                name="Tu juego"
                dataKey="value"
                stroke={ACCENT}
                strokeWidth={1.8}
                fill={ACCENT}
                fillOpacity={0.32}
                dot={{ r: 4, fill: ACCENT, stroke: ACCENT }}
                isAnimationActive
                animationDuration={900}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
